/**
 * Pure display-resolution helpers for the Broker Order Log.
 *
 * The backend already produces broker-neutral `broker_status` /
 * `broker_reason` display fields (see `project_broker_order_display` in
 * `app/brokers/order_log_projection.py`) from durable, per-broker mapped
 * evidence. This module only chooses between the broker-native fields and
 * the existing internal fields for display, and classifies the resulting
 * status text for badge styling — it never parses `raw_response` or any
 * broker-specific payload.
 */

const REJECTED_BADGE_STATUSES = new Set(['FAILED', 'REJECT', 'REJECTED', 'RMS_REJECTED', 'CANCEL_REJECTED']);
const FILLED_BADGE_STATUSES = new Set(['SUCCESS', 'COMPLETE', 'COMPLETED', 'FILLED']);
const PENDING_BADGE_STATUSES = new Set(['PENDING']);

const BEAR_BADGE_CLASS = 'bg-bear/10 text-bear border border-bear/20';
const BULL_BADGE_CLASS = 'bg-bull/10 text-bull border border-bull/20';
const WARN_BADGE_CLASS = 'bg-warn/10 text-warn border border-warn/20';
const NEUTRAL_BADGE_CLASS = 'bg-bg-surface text-txt-muted border border-bg-border';

/** User-facing status: prefer broker-native `broker_status`, else fall back to `status`. */
export function resolveOrderLogStatus(row) {
  return row?.broker_status || row?.status || null;
}

/** User-facing reason: prefer broker-native `broker_reason`, else fall back to `error_msg`. */
export function resolveOrderLogReason(row) {
  return row?.broker_reason || row?.error_msg || null;
}

/**
 * Badge styling class for a resolved status string. `CANCELLED`/`CANCELED`
 * intentionally fall through to the neutral class: this table has no
 * existing cancelled semantic, and this task only adds broker-native
 * rejection/fill/pending classification.
 */
export function orderLogStatusBadgeClass(status) {
  const key = String(status || '').toUpperCase();
  if (REJECTED_BADGE_STATUSES.has(key)) return BEAR_BADGE_CLASS;
  if (FILLED_BADGE_STATUSES.has(key)) return BULL_BADGE_CLASS;
  if (PENDING_BADGE_STATUSES.has(key)) return WARN_BADGE_CLASS;
  return NEUTRAL_BADGE_CLASS;
}
